import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";

export interface DataTableColumn<T> {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  /** Extracts a comparable value for sorting — presentation-only ordering, never a computed business value. */
  sortValue?: (row: T) => string | number;
  render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  getRowKey: (row: T) => string;
  maxHeight?: number;
  ariaLabel: string;
}

/**
 * Generic responsive, sortable, paginated table shell shared by Holdings
 * and Positions. Sorting/pagination reorder and slice already-fetched
 * rows only — no value is recomputed or aggregated.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  maxHeight = 480,
  ariaLabel,
}: DataTableProps<T>): React.JSX.Element {
  const [orderBy, setOrderBy] = React.useState<string | null>(null);
  const [orderDirection, setOrderDirection] = React.useState<"asc" | "desc">("asc");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const sortedRows = React.useMemo(() => {
    if (!orderBy) return rows;
    const column = columns.find((c) => c.key === orderBy);
    if (!column?.sortValue) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      if (av < bv) return orderDirection === "asc" ? -1 : 1;
      if (av > bv) return orderDirection === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, orderBy, orderDirection, columns]);

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key: string) => {
    if (orderBy === key) {
      setOrderDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrderDirection("asc");
    }
  };

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight, overflowX: "auto" }}>
        <Table stickyHeader size="small" aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align ?? "left"}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.key}
                      direction={orderBy === column.key ? orderDirection : "asc"}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row) => (
              <TableRow key={getRowKey(row)} hover>
                {columns.map((column) => (
                  <React.Fragment key={column.key}>{column.render(row)}</React.Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Paper>
  );
}
